import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import ScrollReveal from "@/components/ScrollReveal"
import WaveSeparator from "@/components/WaveSeparator"
import { Button } from "@/components/ui/je-button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { Upload, CheckCircle, FileText, Loader2 } from "lucide-react"
import { useLanguage } from "@/contexts/LanguageContext"
import { useSEO } from "@/hooks/use-seo"
import { useBotProtection } from "@/hooks/use-bot-protection"
import { Honeypot } from "@/components/ui/honeypot"
import { supabase } from "@/integrations/supabase/client"

const Rejoindre = () => {
  const { t } = useLanguage()
  
  useSEO({
    title: `${t('join.title')} — Junior Entreprise HEPIA`,
    description: t('join.seo.description'),
    canonical: 'https://je-hepia.ch/rejoindre'
  })

  const [cvFile, setCvFile] = useState<File | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isUploadingCV, setIsUploadingCV] = useState(false)
  const { toast } = useToast()
  
  const {
    isSubmitting,
    handleHoneypotChange,
    submitForm,
    honeypotValue,
    enableHoneypot
  } = useBotProtection()

  const formSchema = z.object({
    section: z.string({
      required_error: t('join.validation.section'),
    }),
    nom: z.string().min(2, {
      message: t('join.validation.name.min'),
    }),
    prenom: z.string().min(2, {
      message: t('join.validation.firstname.min'),
    }),
    telephone: z.string().min(10, {
      message: t('join.validation.phone.min'),
    }),
    motivation: z.string().min(50, {
      message: t('join.validation.motivation.min'),
    }),
  })

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      section: "",
      nom: "",
      prenom: "",
      telephone: "",
      motivation: "",
    },
  })

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Erreur",
          description: t('join.error.pdf'),
          variant: "destructive",
        })
        return
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        toast({
          title: "Erreur",
          description: t('join.error.size'),
          variant: "destructive",
        })
        return
      }
      setCvFile(file)
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!cvFile) {
      toast({
        title: "CV requis",
        description: t('join.error.cv.required'),
        variant: "destructive",
      })
      return
    }
    
    setIsUploadingCV(true)
    
    try {
      // Generate unique filename
      const timestamp = Date.now()
      const sanitizedName = cvFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const cvPath = `${values.prenom}_${values.nom}_${timestamp}_${sanitizedName}`
      
      // Upload CV to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('cvs')
        .upload(cvPath, cvFile, {
          cacheControl: '3600',
          upsert: false
        })
      
      if (uploadError) {
        console.error('CV upload error:', uploadError)
        toast({
          title: "Erreur d'upload",
          description: "Impossible d'uploader le CV. Veuillez réessayer.",
          variant: "destructive",
        })
        setIsUploadingCV(false)
        return
      }
      
      const data = {
        type: 'student' as const,
        nom: values.nom,
        prenom: values.prenom,
        telephone: values.telephone,
        section: values.section,
        motivation: values.motivation,
        cv_filename: cvFile.name,
        cv_path: cvPath,
      }

      const result = await submitForm(data)
      
      if (result.success) {
        setIsSubmitted(true)
        toast({
          title: "Succès !",
          description: result.message,
        })
      } else {
        toast({
          title: "Erreur",
          description: result.error,
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Submit error:', error)
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      })
    } finally {
      setIsUploadingCV(false)
    }
  }

  const sections = [
    { value: "informatique", label: t('join.sections.computer') },
    { value: "electronique", label: t('join.sections.electronics') },
    { value: "microtechniques", label: t('join.sections.microtechnics') },
    { value: "genie-mecanique", label: t('join.sections.mechanical') },
    { value: "architecture", label: t('join.sections.architecture') },
    { value: "genie-civil", label: t('join.sections.civil') },
    { value: "autre", label: t('join.sections.other') },
  ]

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <ScrollReveal />
        <Navigation />
        <main className="pt-16">
          <section className="py-24 bg-gradient-hero">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <CheckCircle className="w-24 h-24 text-je-red mx-auto mb-8" />
              <h1 className="text-section-title text-je-black mb-6">
                {t('join.success.title')}
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                {t('join.success.subtitle')}
              </p>
              <Button asChild size="lg" className="btn-modern">
                <a href="/">{t('join.success.button')}</a>
              </Button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <ScrollReveal />
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-section-title text-je-black mb-6">
              {t('join.hero.title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t('join.hero.subtitle')}
            </p>
          </div>
        </section>

        <WaveSeparator />

        {/* Formulaire Section */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card-modern p-8 md:p-12">
              <div className="text-center mb-12">
                <h2 className="text-card-title text-je-black mb-4">
                  {t('join.form.title')}
                </h2>
                <p className="text-muted-foreground">
                  {t('join.form.subtitle')}
                </p>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  {/* Honeypot field */}
                  {enableHoneypot && (
                    <Honeypot 
                      value={honeypotValue}
                      onChange={handleHoneypotChange}
                    />
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Section */}
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="section"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-je-black font-semibold">
                              {t('join.field')} *
                            </FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 border-je-gray-medium focus:border-je-red">
                                  <SelectValue placeholder={t('join.field.placeholder')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background border-je-gray-medium">
                                {sections.map((section) => (
                                  <SelectItem 
                                    key={section.value} 
                                    value={section.value}
                                    className="hover:bg-je-red-light focus:bg-je-red-light"
                                  >
                                    {section.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Prénom */}
                    <FormField
                      control={form.control}
                      name="prenom"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            {t('contact.firstname')} *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder={t('contact.firstname')} 
                              className="h-12 border-je-gray-medium focus:border-je-red"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Nom */}
                    <FormField
                      control={form.control}
                      name="nom"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            {t('contact.name')} *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder={t('contact.name')} 
                              className="h-12 border-je-gray-medium focus:border-je-red"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Téléphone */}
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="telephone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-je-black font-semibold">
                              {t('contact.phone')} *
                            </FormLabel>
                            <FormControl>
                              <Input 
                                placeholder={t('contact.phone')} 
                                className="h-12 border-je-gray-medium focus:border-je-red"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Message de motivation */}
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="motivation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-je-black font-semibold">
                              {t('join.motivation')} *
                            </FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder={t('join.motivation.placeholder')}
                                className="min-h-32 border-je-gray-medium focus:border-je-red resize-none"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription className="text-muted-foreground">
                              {t('join.motivation.min')}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Upload CV */}
                    <div className="md:col-span-2">
                      <div className="space-y-2">
                        <label className="text-je-black font-semibold">
                          {t('join.cv')} *
                        </label>
                        <div className="border-2 border-dashed border-je-gray-medium rounded-lg p-6 text-center hover:border-je-red transition-colors">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="cv-upload"
                          />
                          <label
                            htmlFor="cv-upload"
                            className="cursor-pointer flex flex-col items-center space-y-3"
                          >
                            {cvFile ? (
                              <>
                                <FileText className="w-12 h-12 text-je-red" />
                                <div className="text-je-black font-medium">
                                  {cvFile.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {t('join.cv.change')}
                                </div>
                              </>
                            ) : (
                              <>
                                <Upload className="w-12 h-12 text-je-gray-medium" />
                                <div className="text-je-black font-medium">
                                  {t('join.cv.upload')}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {t('join.cv.format')}
                                </div>
                              </>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confidentialité */}
                  <div className="bg-je-gray-light p-6 rounded-lg">
                    <h3 className="text-je-black font-semibold mb-3">
                      {t('join.privacy.title')}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {t('join.privacy.text')}
                    </p>
                  </div>

                  {/* Bouton de soumission */}
                  <div className="text-center">
                    <Button
                      type="submit"
                      size="xl"
                      className="btn-modern min-w-[280px]"
                      disabled={isSubmitting || isUploadingCV}
                    >
                      {isUploadingCV ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Upload du CV...
                        </>
                      ) : isSubmitting ? (
                        t('join.submitting')
                      ) : (
                        t('join.submit')
                      )}
                    </Button>
                  </div>

                </form>
              </Form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Rejoindre